import { companyRequest } from '@/company/api';
import { apiUrl, authHeaders } from '@/lib/shell';
import { workspaceErrorCopy } from './workspace-errors';
import {
  BUSINESS_FILE_ACCEPT,
  BUSINESS_FILE_MIMES,
} from '../../src/company-workspace/data/business-file-types.mjs';
import type {
  CompanyConnections,
  CompanyFile,
  CompanyMeeting,
  CompanyMember,
  CompanyResult,
  CompanySkill,
  CompanySource,
  SkillSnapshot,
  WorkspaceData,
  WorkspaceRun,
} from './types';

const root = '/api/v1/company';
const segment = (id: string) => encodeURIComponent(id);
export const workspaceApi = {
  list: () => companyRequest<WorkspaceData>(`${root}/workspace`),
  /** A saved Page's signed address (/everywhere/pages/…) for the canvas to frame. */
  pageAddress: (id: string) =>
    companyRequest<{ path: string }>(`${root}/results/${segment(id)}/page`).then((r) => r.path),
  references: (input: { fileIds: string[]; sourceIds: string[]; resultIds: string[] }) => {
    const params = new URLSearchParams(
      Object.entries(input).map(([key, ids]) => [key, ids.join(',')]),
    );
    return companyRequest<import('./selected-references').ReferenceMetadata>(
      `${root}/references?${params}`,
    );
  },
  /** One page of meetings. The list is company-wide and says whether more exist. */
  meetingsPage: (offset = 0) =>
    companyRequest<{ meetings: CompanyMeeting[]; hasMore?: boolean; nextOffset?: number | null }>(
      offset ? `${root}/meetings?offset=${offset}` : `${root}/meetings`,
    ),
  members: () =>
    companyRequest<{ members: CompanyMember[] }>(`${root}/members`).then((r) => r.members),
  meeting: (id: string) =>
    companyRequest<{ meeting: CompanyMeeting }>(`${root}/meetings/${segment(id)}`).then(
      (r) => r.meeting,
    ),
  createMeeting: (input: {
    fileId: string;
    title?: string;
    language: 'auto' | 'sv' | 'en';
    captureMethod: 'upload' | 'browser';
    participants: (
      | { kind: 'member'; userId: string }
      | { kind: 'guest'; name: string; email?: string }
    )[];
    /** Who can see it (the file's audience); the server defaults to company. */
    visibility?: 'company' | 'private';
  }) =>
    companyRequest<{ meeting: CompanyMeeting }>(`${root}/meetings`, 'POST', input).then(
      (r) => r.meeting,
    ),
  retryMeeting: (id: string) =>
    companyRequest<{ meeting: CompanyMeeting }>(
      `${root}/meetings/${segment(id)}/retry`,
      'POST',
      {},
    ).then((r) => r.meeting),
  updateMeetingSegment: (
    meetingId: string,
    index: number,
    input: { displayText?: string; participantId?: string | null },
  ) =>
    companyRequest<{ segment: import('./types').MeetingSegment }>(
      `${root}/meetings/${segment(meetingId)}/segments/${index}`,
      'PATCH',
      input,
    ).then((r) => r.segment),
  fileDownloadTarget: (id: string, inline = false) =>
    companyRequest<{ url: string | null }>(
      `${root}/files/${segment(id)}/download?format=json${inline ? '&disposition=inline' : ''}`,
    ),
  file: (id: string) =>
    companyRequest<{ file: CompanyFile }>(`${root}/files/${segment(id)}`).then((r) => r.file),
  source: (id: string) =>
    companyRequest<{ source: CompanySource }>(`${root}/sources/${segment(id)}`).then(
      (r) => r.source,
    ),
  skill: (id: string) =>
    companyRequest<{ skill: CompanySkill }>(`${root}/skills/${segment(id)}`).then((r) => r.skill),
  result: (id: string) =>
    companyRequest<{ result: CompanyResult }>(`${root}/results/${segment(id)}`).then(
      (r) => r.result,
    ),
  run: (id: string) =>
    companyRequest<{ run: WorkspaceRun }>(`${root}/runs/${segment(id)}`).then((r) => r.run),
  revisions: (id: string) =>
    companyRequest<{ revisions: SkillSnapshot[] }>(`${root}/skills/${segment(id)}/revisions`).then(
      (r) => r.revisions,
    ),
  // The original bytes are the body, so large CSVs and PDFs are neither
  // re-encoded as JSON nor held twice in the browser.
  upload: async ({
    file,
    mimeType,
    includeInKnowledge,
    signal,
  }: {
    file: Blob & { name: string };
    mimeType: string;
    includeInKnowledge: boolean;
    signal?: AbortSignal;
  }) => {
    const params = new URLSearchParams({
      name: file.name,
      includeInKnowledge: String(includeInKnowledge),
    });
    const response = await fetch(apiUrl(`${root}/files/upload?${params}`), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { ...authHeaders(), 'Content-Type': mimeType },
      body: file,
      signal,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const code = error.error ?? error.code;
      // A payment refusal says the server's own sentence (plan, credits or limit).
      throw new Error(
        response.status === 402 && code === 'payment_required' && typeof error.message === 'string'
          ? error.message
          : uploadErrorMessage(file.name, response.status, code),
      );
    }
    return ((await response.json()) as { file: CompanyFile }).file;
  },
  restoreSkill: (id: string, revision: number, snapshot: SkillSnapshot) =>
    companyRequest<{ skill: CompanySkill }>(`${root}/skills/${segment(id)}`, 'PATCH', {
      revision,
      name: snapshot.name,
      description: snapshot.description,
      instructions: snapshot.instructions,
      modelChoice: snapshot.modelChoice,
      fileIds: snapshot.fileIds,
      sourceIds: snapshot.sourceIds,
    }),
  /** A publisher publishes at once; anyone else's request waits in a publisher's Inbox. No preview is needed. */
  publishSkill: (id: string, revision: number) =>
    companyRequest<{ skill?: CompanySkill; status?: 'applied' | 'pending' }>(
      `${root}/skills/${segment(id)}/publish`,
      'POST',
      { revision, confirmed: true },
    ),
  deleteSkill: (id: string) =>
    companyRequest(`${root}/skills/${segment(id)}`, 'DELETE', { confirmed: true }),
  archivedSkills: (offset = 0) =>
    companyRequest<{
      skills: import('./skill-archive').ArchivedSkill[];
      nextOffset: number | null;
    }>(`${root}/skills/archive?offset=${offset}`),
  restoreArchivedSkill: (id: string) =>
    companyRequest(`${root}/skills/${segment(id)}/restore`, 'POST', { confirmed: true }),
  unpublishSkill: (id: string) =>
    companyRequest<{ skill: CompanySkill }>(`${root}/skills/${segment(id)}/unpublish`, 'POST', {
      confirmed: true,
    }),
  connections: () =>
    companyRequest<{ connections: CompanyConnections }>(`${root}/connections`).then(
      (r) => r.connections,
    ),
  // The credential plane (Nango) hosts the authorization page; the popup opens
  // this link and the app reconciles afterwards — the popup's word is not trusted.
  connectionSession: (provider: string) =>
    companyRequest<{ provider: string; connectLink: string; expiresAt: string }>(
      `${root}/connections/${segment(provider)}/session`,
      'POST',
      {},
    ),
  reconcileConnections: () =>
    companyRequest<{ connections: CompanyConnections }>(
      `${root}/connections/reconcile`,
      'POST',
      {},
    ).then((r) => r.connections),
  disconnect: (id: string) => companyRequest(`${root}/connections/${segment(id)}`, 'DELETE'),
};

// Mirrors MAX_FILE_BYTES in src/company-workspace/data/contract.mjs.
export const MAX_UPLOAD_BYTES = 500_000_000;
export const LEGACY_UPLOAD_BYTES = 50 * 1024 * 1024;
export const UPLOAD_ACCEPT = `${BUSINESS_FILE_ACCEPT},.pdf,.txt,.md,.csv,.json,.vtt,.srt,.m4a,.mp3,.wav,.webm,.ogg,.mp4`;
export const UPLOAD_RULE =
  'Use Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, text, JSON, transcripts, audio or video up to 500 MB. Save older Office files as .xlsx, .docx or .pptx first.';

const UPLOAD_MIMES: Readonly<Record<string, string>> = {
  ...BUSINESS_FILE_MIMES,
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
  vtt: 'text/vtt',
  srt: 'application/x-subrip',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  webm: 'audio/webm',
  ogg: 'audio/ogg',
  mp4: 'video/mp4',
};

export function uploadMime(name: string): string | null {
  const extension = name.toLowerCase().split('.').at(-1);
  return Object.hasOwn(UPLOAD_MIMES, extension ?? '') ? UPLOAD_MIMES[extension ?? ''] : null;
}

/** The extension an upload of this media type carries ("application/pdf" → "pdf"); null for one we do not take. */
export function uploadExtension(mimeType: string): string | null {
  return Object.entries(UPLOAD_MIMES).find(([, mime]) => mime === mimeType)?.[0] ?? null;
}

const UPLOAD_ERROR_KEYS = {
  office_unreadable: 'office_unreadable',
  office_encrypted: 'office_encrypted',
  office_has_no_text: 'office_has_no_text',
  office_too_complex: 'office_too_complex',
  office_extraction_timeout: 'office_extraction_timeout',
  file_text_too_large: 'file_text_too_large',
  pdf_has_no_text: 'pdf_has_no_text',
  pdf_encrypted: 'pdf_encrypted',
  pdf_unreadable: 'pdf_unreadable',
  pdf_too_many_pages: 'pdf_too_many_pages',
  pdf_too_complex: 'pdf_too_complex',
  file_too_complex: 'file_too_complex',
  pdf_extraction_timeout: 'pdf_extraction_timeout',
  file_too_large: 'file_too_large',
  FST_ERR_CTP_BODY_TOO_LARGE: 'file_too_large',
  invalid_workspace_input: 'invalid_workspace_input',
  workspace_storage_full: 'workspace_storage_full',
  workspace_capacity_reached: 'workspace_capacity_reached',
  workspace_upload_busy: 'workspace_upload_busy',
  workspace_storage_unavailable: 'workspace_storage_unavailable',
  company_admin_required: 'company_admin_required',
} as const;

export function uploadRuleMessage(locale?: string) {
  return workspaceErrorCopy(locale).uploadRule;
}
export function uploadErrorMessage(name: string, status: number, code?: string, locale?: string) {
  const copy = workspaceErrorCopy(locale) as Record<string, string>;
  const key = code && UPLOAD_ERROR_KEYS[code as keyof typeof UPLOAD_ERROR_KEYS];
  if (key && copy[key]) return copy[key].replaceAll('{name}', name);
  if (status === 413) return copy.uploadTooLarge.replaceAll('{name}', name);
  return copy.uploadGeneric.replaceAll('{name}', name);
}
export function downloadText(name: string, value: string, type = 'text/plain') {
  const url = URL.createObjectURL(new Blob([value], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// A presentation is rendered on demand from the saved deck, so unlike a file
// download there is nothing stored to stream — the route builds it per request.
export async function downloadDeck(result: { id: string; title: string }, locale?: string) {
  const response = await fetch(apiUrl(`${root}/results/${segment(result.id)}/pptx`), {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(workspaceErrorCopy(locale).downloadDeck);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  // A blob URL carries no headers, so the anchor needs its own name — but the
  // server already sanitised one into content-disposition. Read that rather
  // than re-deriving it here, which is how the two came to disagree.
  link.download = dispositionFilename(response.headers.get('content-disposition'));
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// `attachment; filename="presentation.pptx"; filename*=UTF-8''Autumn%20review.pptx`
function dispositionFilename(header: string | null) {
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (!encoded) return 'presentation.pptx';
  try {
    return decodeURIComponent(encoded) || 'presentation.pptx';
  } catch {
    return 'presentation.pptx';
  }
}
export async function downloadFile(file: CompanyFile, locale?: string) {
  const target = await workspaceApi.fileDownloadTarget(file.id);
  if (target.url) {
    const link = document.createElement('a');
    link.href = target.url;
    link.download = file.name;
    link.rel = 'noopener';
    link.click();
    return;
  }
  const response = await fetch(apiUrl(`${root}/files/${segment(file.id)}/download`), {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(workspaceErrorCopy(locale).downloadFile);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
